<template>
	<div
		id="full-page-mode"
		class="app"
		:class="{
			mobile: isMobileWebApp,
			landscape: isLandscape,
			portrait: isMobilePortrait,
			fullscreen: isFullscreen,
			'has-cropping-enabled': videoCroppingEnabled,
		}"
	>
		<Layout
			class="layout-w"
			:class="{
				'couchmode-open': isCouch,
			}"
			:is-couch="isCouch"
			:show-quality-menu="showQualityMenu"
			:is-cinema-mode="isCinemaMode"
		>
			<template #media>
				<EventEmbedPreview
					v-if="showEventEmbedPreview"
					class="event-embed-preview"
					:event="event"
					:show-infos="false"
					:show-countdown="true"
				/>
				<ErrorScreen
					v-else-if="hasErrorScreen"
					:info="errorScreenInformation"
					:screen="errorScreen"
				/>
				<PlayerWrapper
					v-else-if="!hasPlayoutError"
					ref="player"
					:autoplay="autoplay && !isMobileAppleDevice"
				/>
			</template>
			<template
				v-if="!isFullscreen && hasTimelineTags && !isCouch && !playerIsAdMode"
				#timeline
			>
				<div class="timeline-wrapper">
					<TimelineBlock
						v-show="!hasErrorScreen && !showEventEmbedPreview"
						:duration="duration"
						:elapsed="position"
						:is-looking-back="isLookingBackInLiveEvent"
					/>
				</div>
			</template>
			<template #meta>
				<div class="meta-wrapper">
					<div class="meta">
						<div class="info">
							<h1 class="title">
								{{ info.name }}
							</h1>
							<div class="subtitle">
								<span
									v-if="info.eventContext"
									class="event-context"
								> {{ info.eventContext }} &bull; </span>
								<span v-if="info.parentCategory"> {{ info.parentCategory }} &bull; </span>
								<span v-if="info.childCategory"> {{ info.childCategory }} &bull; </span>
								<span v-if="info.eventDateTime">{{ info.eventDateTime }}</span>
								<span v-if="info.location"> &bull; {{ info.location }} </span>
							</div>
						</div>
						<div class="video-actions">
							<div
								v-if="videoCroppingEnabled"
								class="crop-link"
								@click="handleCropLink"
							>
								<Icon
									icon-name="formatconverter"
									size="lg"
								/>
								<span>Clip your moment</span>
							</div>
							<button
								v-if="$arenaConfig.followingEnabled && isLoggedIn && isEventPublished"
								@click="isBookmarked ? unbookmarkEvent({ event }) : bookmarkEvent({ event })"
							>
								<Tooltip
									:text="isBookmarked ? $I18n.trans('event.messages.remove_from_saved') : $I18n.trans('event.messages.save_event')"
									:delay="1000"
									position="top-center"
								>
									<template #toggle>
										<Icon
											:icon-name="isBookmarked ? 'bookmark-filled' : 'bookmark-outline'"
											size="lg"
										/>
									</template>
								</Tooltip>
							</button>
							<VideoDownload
								v-if="videoDownloadEnabled"
								:event="event"
								:login-restricted="requiresLoginForDownload"
								:is-logged-in="isLoggedIn"
							/>
							<button
								v-if="isSharingEnabled && !isUpcoming && !event?.publishEnded"
								class="share"
								data-testid="share-event-button"
								@click.stop="handleShare"
							>
								<Icon
									icon-name="network-communication"
									size="lg"
								/>
							</button>
						</div>
					</div>
				</div>
			</template>
			<template #participants>
				<ParticipantsHorizontalList :participants="participants" />
			</template>
			<template #ads>
				<div
					v-if="adBanner && isMobileWebApp"
					class="x-image"
				>
					<AdButlerAd
						v-if="adBanner.isAdbutlerAd"
						:ad="adBanner"
						class="x-image__inner-w"
						:class="{ 'has-loaded': adImageLoaded }"
						:data-text="$I18n.trans('monetization.advertisement')"
						@loaded="() => adImageLoaded = true"
					/>
					<component
						:is="eventBannerAd.link ? 'a' : 'div'"
						v-else-if="eventBannerAd.imageUrl"
						:href="eventBannerAd.link"
						:target="eventBannerAd.link ? '_blank' : null"
						rel="noopener"
						class="x-image__inner-w"
						:class="{ 'has-loaded': adImageLoaded }"
						:data-text="$I18n.trans('monetization.advertisement')"
					>
						<img
							ref="adImage"
							:src="eventBannerAd.imageUrl"
							class="x-image__image"
						>
					</component>
				</div>
			</template>
			<template #description>
				<div
					class="s-html -no-p-margin"
					v-html="eventDescription"
				/>
			</template>
			<template #actions>
				<ActionBlock
					:player-type="playerType"
				/>
			</template>
		</Layout>
		<!-- share -->
		<ShareModal
			v-if="showShare && !isUpcoming"
			class="custom-modals"
			is-event
			:title="shareTitle"
			:event-title="eventTitle"
			:item="shareItem"
			:item-label="itemLabel"
			:links="shareLinks"
			:url="shareUrl"
			:url-label="urlLabel"
			:image-not-found-label="imageNotFoundLabel"
			@close="handleShareClose"
		/>
		<!-- context menu mobile -->
		<ContextMenuMobile
			v-if="isMobileWebApp && !isUpcoming"
			class="custom-modals"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import Layout from '@videotools/js/full-page-mode/components/Layout';
import Player from '@components/player';
import ActionBlock from '@videotools/js/blocks/Action';
import EventEmbedPreview from '@components/EventEmbedPreview';
import ShareModal from '@components/share-modal';
import ErrorScreen from '@components/ErrorScreen';
import PlayerWrapper from '@components/player/PlayerWrapper';
import TimelineBlock from '@videotools/js/blocks/Timeline';
import VideoDownload from '@shared/components/VideoDownload';
import PlayerContentInfo from '@components/player/PlayerContentInfo';
import Tooltip from '@shared/components/Tooltip';
import { ContextMenuMobile } from '@components/context-menu';
import playerTypes from '@videotools/js/constants/playerTypes';
import { MAX_INTERVAL } from '@videotools/js/constants/app';
import { bus, disableHeartbeat, enableHeartbeat, EVENT, playerBus } from '@videotools/js/events';
import ParticipantsHorizontalList from '@components/Participant/ParticipantsHorizontalList';
import AdButlerAd from '@components/Ads/AdButlerAd.vue';
import { getLeagueName, getLocation } from '@shared/utils/eventUtils';
import { getDateTime } from '@shared/utils/timeUtils';
import { CategoryChild } from '@shared/enums/CategoryChild';
import { AdType } from '@shared/enums/AdType';

import laroute from '@laroute';
import storage from '@shared/js/storage';
import { CategoryParent } from '@shared/enums/CategoryParent';
import { EventType } from '@shared/enums/EventType';
import TrackingService from '@shared/js/TrackingService';
import { AnalyticsEvent } from '@shared/enums/AnalyticsEvent';

const LIVE_LOOKING_BACK_DELTA = 20;

const SHARE_TYPE = {
	EVENT: 'event',
	TAG:   'tag',
	CLIP:  'clip',
};
const events = [
	EVENT.SHARE_TAG,
	EVENT.SHARE_CLIP,
	EVENT.SHARE_EVENT,
	EVENT.PLAYER.ON.PROGRESS,
	EVENT.PLAYER.ON.DURATION,
	EVENT.NETWORK_UNFORCED_CLIENT_ERROR,
];

export default {
	name:       'FullPageMode',
	components: {
		ParticipantsHorizontalList,
		Layout,
		Player,
		ActionBlock,
		EventEmbedPreview,
		ShareModal,
		ContextMenuMobile,
		ErrorScreen,
		PlayerWrapper,
		TimelineBlock,
		PlayerContentInfo,
		VideoDownload,
		AdButlerAd,
		Tooltip,
	},
	props: {
		playerType: {
			type:      [String, Object],
			default:   () => null,
			validator: (type) => Object.values(playerTypes).includes(type) || type === null,
		},
	},
	data() {
		return {
			showShare:       false,
			shareItem:       null,
			shareType:       null,
			isUpcoming:      true,
			eventTimer:      null,
			showQualityMenu: false,
			position:        0,
			duration:        0,
			isMounted:       false,
			adImageLoaded:   false,
		};
	},
	computed: {
		...mapGetters({
			isCouch:                  'app/isInCouchMode',
			isCinemaMode:             'app/isCinemaMode',
			isMobileWebApp:           'app/isMobileWebApp',
			isMobileWebAppPortrait:   'app/isMobileWebAppPortrait',
			isLandscape:              'device/isLandscape',
			isMobilePortrait:         'device/isMobilePortrait',
			isFullscreen:             'device/isFullscreen',
			isMobileAppleDevice:      'device/isMobileAppleDevice',
			isTouchDevice:            'device/isTouchDevice',
			supportsNativeShare:      'device/supportsNativeShare',
			event:                    'event/event',
			ads:                      'event/ads',
			eventTitle:               'event/eventTitle',
			eventURL:                 'event/eventURL',
			adBanner:                 'event/adBanner',
			eventBannerImageUrl:      'event/eventBannerImageUrl',
			hasTimelineTags:          'event/hasTimelineItems',
			streamLeadTimeSeconds:    'event/streamLeadTimeSeconds',
			leagueTable:              'event/leagueTable',
			isLive:                   'event/isLive',
			isPaymentProtected:       'event/isPaymentProtected',
			requiresLoginForCYM:      'event/requiresLoginForCYM',
			requiresLoginForDownload: 'event/requiresLoginForDownload',
			hasAccessToEvent:         'sharedUser/hasAccessToEvent',
			isEmbedded:               'features/isEmbedded',
			autoplay:                 'features/autoplay',
			isSharingEnabled:         'features/isSharingEnabled',
			errorScreen:              'error/errorScreen',
			hasErrorScreen:           'error/hasErrorScreen',
			hasPlayoutError:          'error/hasPlayoutError',
			playerIsAdMode:           'player/isAdMode',
			hasBookmarkedEvent:       'sharedUser/hasBookmarkedEvent',
			isLoggedIn:               'sharedUser/isLoggedIn',
		}),
		leagueCodes() {
			return this.leagueTable?.entries?.data?.map((data) => data.team.club.code) || [];
		},
		shareTitle() {
			switch (this.shareType) {
			case SHARE_TYPE.EVENT:
				return this.shareLabels.SHARE_EVENT;
			case SHARE_TYPE.TAG:
				return this.shareLabels.SHARE_TAG;
			case SHARE_TYPE.CLIP:
				return this.shareLabels.SHARE_CLIP;
			default:
				return this.shareLabels.SHARE;
			}
		},
		parentCategory() {
			// category "other" is not displayed in arena
			if (!this.event?.sport || !this.event?.categoryParent || this.event.categoryParent === CategoryParent.OTHER) {
				return '';
			}
			return this.$Helpers.ucfirst(this.$I18n.trans(`sports.${this.event.sport.name}.categories.parent.${this.event.categoryParent}`));
		},
		childCategory() {
			return (this.event?.sport && this.event?.categoryChild) ? this.$Helpers.ucfirst(this.$I18n.trans(`sports.${this.event.sport.name}.categories.child.${this.event.categoryChild}`)) : '';
		},
		itemLabel() {
			return `${this.parentCategory}${this.childCategory ? ` • ${this.childCategory}` : ''}`;
		},
		shareUrl() {
			if (!this.shareItem) {
				return '';
			}
			return this.shareItem.url;
		},
		shareLinks() {
			return this.shareItem?.shareLinks ?? { facebook: '', twitter: '', mail: '' };
		},
		liveInSeconds() {
			return this.event ? this.event.liveInSeconds : 0;
		},
		urlLabel() {
			const labels = {
				[SHARE_TYPE.TAG]:       this.shareLabels.TAG_URL,
				[SHARE_TYPE.CLIP]:      this.shareLabels.CLIP_URL,
				[SHARE_TYPE.COPY_LINK]: this.shareLabels.COPY_LINK,
			};
			return labels[this.shareType] || this.shareLabels.COPY_LINK;
		},
		imageNotFoundLabel() {
			return this.shareLabels.PREVIEW_NOT_FOUND;
		},
		errorScreenInformation() {
			return {
				name:           this.event.name || this.$I18n.trans('commons.unnamed'),
				eventContext:   this.event.contextable?.name,
				parentCategory: this.parentCategory,
				childCategory:  this.childCategory,
				eventDateTime:  getDateTime(this.event.startTimestamp + (this.event.kickoffOffsetMinutes * 60)),
				location:       getLocation(this.event),
				url:            this.eventURL,
			};
		},
		// On iOS on external embeds we show a preview instead of the player
		// because we want the users to use our "fake" fullscreen mode on our platform
		// by redirecting them
		showEventEmbedPreview() {
			let isExternalEmbed = false;
			try {
				Boolean(parent.document.body);
			} catch {
				isExternalEmbed = true;
			}

			return this.isUpcoming || (isExternalEmbed && this.isMobileAppleDevice);
		},
		isLookingBackInLiveEvent() {
			if (!this.isMounted) return false;
			const playerRef = this.$refs.player;
			if (!playerRef) return false;
			return this.isLive && playerRef.position < playerRef.mediaDuration - LIVE_LOOKING_BACK_DELTA - this.streamLeadTimeSeconds;
		},
		participants() {
			return this.event.participants.map((participant) => {
				const club = participant.participatable.club;

				let url = null;
				if (club && this.leagueCodes.includes(club?.code)) {
					url = laroute.route('arena.team', { team: participant.participatable.id, slug: this.$Helpers.slugify(participant.participatable.name) });
				}

				return {
					id:    participant.id,
					image: participant.participatable.imageUrl,
					code:  participant.participatable.club?.code,
					name:  participant.participatable.name,
					url,
				};
			});
		},
		info() {
			const leagueName = getLeagueName(this.event) || this.childCategory;

			return {
				name:           this.event.name || this.$I18n.trans('commons.unnamed'),
				eventContext:   this.event.contextable?.name,
				parentCategory: this.parentCategory,
				childCategory:  this.event.categoryChild === CategoryChild.MATCH_LEAGUE ? leagueName : this.childCategory,
				eventDateTime:  getDateTime(this.event.startTimestamp + (this.event.kickoffOffsetMinutes * 60)),
				location:       getLocation(this.event),
			};
		},
		eventDescription() {
			return this.event.description;
		},
		videoDownloadEnabled() {
			const hasNoAccessToEvent = this.event.isPaymentProtected && !this.hasAccessToEvent(this.event);
			return this.event?.downloadable && this.isEventPublished && !hasNoAccessToEvent && !this.playerIsAdMode && (this.$arenaConfig.videoDownloadEnabled || this.event?.arenaDownloadEnabled);
		},
		videoCroppingEnabled() {
			return this.event?.videoAvailable && !this.isUpcoming && this.isEventPublished && !this.playerIsAdMode && this.event?.arenaCroppingEnabled;
		},
		eventBannerAd() {
			return this.ads.find(ad => ad.type === AdType.EVENT_BANNER);
		},
		isBookmarked() {
			return this.hasBookmarkedEvent(this.event);
		},
		isEventPublished() {
			return !this.event?.publishEnded && !this.event?.publishNotStarted;
		},
	},
	watch: {
		liveInSeconds(value) {
			if (!Number.isFinite(value)) return;

			this.isUpcoming = value > 0;
			if (this.isUpcoming) {
				clearTimeout(this.eventTimer);
				this.waitForLiveEvent(value * 1000);
			}
		},
	},
	created() {
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}

		if (this.playerType) {
			this.setPlayerType(this.playerType);
		}

		enableHeartbeat();
	},
	mounted() {
		this.isUpcoming = this.event.type === EventType.EVENT && this.event?.liveInSeconds > 0;

		if (this.isUpcoming) {
			this.waitForLiveEvent(this.event.liveInSeconds * 1000);
		}

		this.isMounted = true;

		if (this.eventBannerAd?.imageUrl && this.$refs.adImage) {
			this.$refs.adImage.addEventListener('load', () => {
				this.adImageLoaded = true;
			});
		}

		try {
			storage.trackVisitedSport(this.event.sport.id);
		} catch (error) {
			console.log(error);
		}
	},
	destroyed() {
		disableHeartbeat();

		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		...mapActions({
			setPlayerType:   'app/setPlayerType',
			shareNative:     'device/shareNative',
			bookmarkEvent:   'sharedUser/bookmarkEvent',
			unbookmarkEvent: 'sharedUser/unbookmarkEvent',
		}),
		[`BUS-${EVENT.SHARE_CLIP}`](clip) {
			this.shareItem = clip;
			this.shareType = SHARE_TYPE.CLIP;
			this.shareNativeOrOpenShareModal({
				url: this.shareUrl,
			});
		},
		[`BUS-${EVENT.SHARE_TAG}`](tag) {
			this.shareItem = tag;
			this.shareType = SHARE_TYPE.TAG;
			this.shareNativeOrOpenShareModal({
				url: this.shareUrl,
			});
		},
		[`BUS-${EVENT.SHARE_EVENT}`]() {
			this.shareItem = this.event;
			this.shareType = SHARE_TYPE.EVENT;
			this.shareNativeOrOpenShareModal({
				url:  this.shareUrl,
				text: this.eventTitle,
			});
		},
		shareNativeOrOpenShareModal(data) {
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative(data);
			} else {
				this.showShare = true;
			}
			TrackingService.trackEvent(this.event.id, AnalyticsEvent.EVENT_SHARE);
		},
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](value) {
			this.position = value;
		},
		[`BUS-${EVENT.PLAYER.ON.DURATION}`](value) {
			this.duration = value;
		},
		[`BUS-${EVENT.NETWORK_UNFORCED_CLIENT_ERROR}`]() {
			window.toast.warning(this.$I18n.trans('errors.client.network_error'));
		},
		handleShare() {
			bus.publish(EVENT.SHARE_EVENT);
		},
		handleShareClose() {
			this.showShare = false;
		},
		waitForLiveEvent(liveInMiliSeconds) {
			if (liveInMiliSeconds > MAX_INTERVAL) {
				this.eventTimer = setTimeout(() => {
					this.waitForLiveEvent(liveInMiliSeconds - MAX_INTERVAL);
				}, MAX_INTERVAL);
			} else {
				this.eventTimer = setTimeout(() => {
					this.isUpcoming = false;
				}, liveInMiliSeconds);
			}
		},
		handleCropLink() {
			if (this.requiresLoginForCYM && !this.isLoggedIn) {
				this.eventBus.$emit('modal:LoginWallModal', {
					title:    this.$I18n.trans('event.multiformat_editor.tooltip'),
					text:     this.$I18n.trans('arena.feature_access.login_wall.text_feature'),
					closable: true,
				});
				return;
			}

			playerBus.pause();
			const url = laroute.route('arena.event.multiformat', { event: this.event.id });
			const query = new URLSearchParams({
				position: `${this.position}`,
			});
			const currentAudioTrackId = playerBus.player?.currentAudioTrackId;
			if (Number.isFinite(currentAudioTrackId)) {
				query.set('audioTrackId', `${currentAudioTrackId}`);
			}

			window.open(`${url}?${query.toString()}`, '_blank');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

#full-page-mode {
	width: 100%;
	height: 100%;
	color: var(--color-soft-white);

	body.-t-light & {
		color: var(--color-black);
	}

	&:not(.fullscreen) {
		@include size(md-up) {
			padding-top: 24px;
		}
	}

	&.mobile {
		.meta {
			padding: 16px;

			.info {
				margin-right: 18px;

				.title {
					@include text-md-2(true);
					margin-bottom: 4px;
				}
				.subtitle {
					@include text-sm-1;
				}
			}
		}
	}

	.event-embed-preview {
		height: 100%;
		width: 100%;
	}

	.custom-modals {
		position: absolute;
		width: 100%;
		bottom: 0;
		left: 0;
		height: auto;
		z-index: 999;
	}

	.layout-w:not(.couchmode-open)::v-deep .top-drawer {
		position: relative;
		top: auto;
		left: auto;
	}

	.meta {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex: 1;
		padding: 24px;
		flex-wrap: wrap;
		gap: 12px;
		border-bottom: 1px solid var(--color-grey-900);

		.info {
			flex: 1;
			display: flex;
			flex-direction: column;
			margin-right: 20px;

			.title {
				font-size: 24px;
				font-weight: 500;
				line-height: 32px;
				margin-bottom: 8px;

				body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
					font-weight: 600;
				}
				body.-t-phygital & {
					font-family: $font-stack-phygital;
				}
			}

			.subtitle {
				font-size: 16px;
				color: var(--color-grey-300);
			}
		}

		.share {
			align-self: flex-start;
			width: 24px;
			height: 24px;
			cursor: pointer;
		}
	}

	.video-actions {
		display: flex;
		align-self: flex-start;
		gap: 16px;
	}

	.timeline-wrapper {
		padding: 20px 0;
		border-bottom: 1px solid var(--color-grey-900);
	}

	&.has-cropping-enabled.mobile {
		.meta {
			flex-direction: column-reverse;
			align-items: flex-start;
		}

		.info {
			min-width: 50%;
		}

		.video-actions {
			position: relative;
			display: flex;
			align-self: flex-end;
			gap: 16px;
			width: 100%;
			padding-bottom: 16px;

			&::after {
				content: '';
				display: block;
				height: 1px;
				width: 100vw;
				background-color: var(--color-grey-900);
				position: absolute;
				bottom: 0;
				left: -16px;
			}
		}
	}
}

.x-image {
	display: none;
	justify-content: center;
	align-items: center;
	padding: 52px 16px 8px;

	@include size(sm-up) {
		padding: 66px 16px 28px;
	}

	#full-page-mode.mobile & {
		display: flex;

		&:empty {
			display: none;
		}
	}
}

.x-image__inner-w {
	position: relative;
	max-width: 300px;

	&.has-loaded::after {
		font-size: 14px; // stylelint-disable-line sh-waqar/declaration-use-variable, property-disallowed-list
		color: var(--color-grey-300);
		display: block;
		content: attr(data-text);
		position: absolute;
		right: 2px;
		bottom: calc(100% - -8px);
	}
}

.x-image__image {
	display: block;
	width: auto;
	max-height: none;
}

.crop-link {
	position: relative;
	display: flex;
	flex-grow: 1;
    justify-content: flex-start;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	padding-right: 24px;
	cursor: pointer;

	svg {
		color: var(--color-hype-yellow);
	}

	span {
		position: relative;

		&::after {
			content: 'spotlight';
			position: absolute;
			right: -9px;
			top: 84%;
			font-size: 11px;
		}
	}

	#full-page-mode:not(.mobile) & {
		&::after {
			content: '';
			display: block;
			width: 1px;
			height: 24px;
			position: absolute;
			right: 0;
			top: 50%;
			transform: translateY(-50%);
			background-color: currentColor;
		}
	}
}

</style>
